/**
 * PmsConnectionPanel — "PMS connecté" sub-tab of the Établissement page.
 * Lets the admin choose the PMS software, set its endpoint and API key,
 * and test/save the connection. Sandbox-only (fake status + toasts).
 */
import React from "react";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Info } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card } from "./shared";
import { PMS_CATALOG } from "./data";

export function PmsConnectionPanel() {
  const [pmsId, setPmsId] = React.useState("medistory");
  const [endpoint, setEndpoint] = React.useState("https://api.medistory.local:8443/interop/v1");
  const [apiKey, setApiKey] = React.useState("mk_sbx_•••••••••••••4c8b");
  const [connected, setConnected] = React.useState(true);
  const [testing, setTesting] = React.useState(false);

  const pms = PMS_CATALOG.find((p) => p.id === pmsId) || PMS_CATALOG[0];

  const testConnection = () => {
    setTesting(true);
    setTimeout(() => {
      setTesting(false);
      setConnected(true);
      toast.success(`Connexion OK · ${pms.name} · Latence 42 ms (sandbox).`);
    }, 700);
  };

  return (
    <Card className="p-5" data-testid="pms-connection-panel">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">PMS connecté</div>
          <div className="text-[16px] font-bold text-slate-900">Logiciel métier de facturation & dossier patient</div>
        </div>
        {connected
          ? <span className="inline-flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200"><CheckCircle2 className="w-3 h-3" /> Connecté</span>
          : <span className="inline-flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-slate-100 text-slate-500 border border-slate-200">Non configuré</span>
        }
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold block mb-1.5">Logiciel</label>
          <select
            value={pmsId}
            onChange={(e) => { setPmsId(e.target.value); setConnected(false); }}
            data-testid="pms-connection-select"
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900 bg-white"
          >
            {PMS_CATALOG.map((p) => (
              <option key={p.id} value={p.id}>{p.name} — {p.vendor}</option>
            ))}
          </select>
          <div className="mt-1 text-[11px] text-slate-500">Choisissez votre PMS parmi les logiciels certifiés ANS.</div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold block mb-1.5">Endpoint API (simulé)</label>
          <input
            value={endpoint}
            onChange={(e) => { setEndpoint(e.target.value); setConnected(false); }}
            data-testid="pms-connection-endpoint"
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] text-slate-900 bg-white font-mono"
          />
          <div className="mt-1 text-[11px] text-slate-500">URL du webservice de transmission (HTTPS / mTLS conseillé).</div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold block mb-1.5">Clé API</label>
          <input
            value={apiKey}
            onChange={(e) => { setApiKey(e.target.value); setConnected(false); }}
            type="password"
            data-testid="pms-connection-apikey"
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] text-slate-900 bg-white font-mono"
          />
          <div className="mt-1 text-[11px] text-slate-500">Stockée chiffrée · rotée tous les 90 jours.</div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold block mb-1.5">Statut</label>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[12.5px] text-slate-700 space-y-1">
            <div>Logiciel : <span className="font-semibold text-slate-900">{pms.name}</span></div>
            <div>Éditeur : {pms.vendor}</div>
            <div>Dernière transmission réussie : <span className="font-semibold">il y a 2 min</span></div>
            <div>Erreurs 24 h : <span className="font-semibold text-emerald-700">0</span></div>
          </div>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-slate-200 flex flex-wrap items-center justify-end gap-2">
        <button
          onClick={testConnection}
          disabled={testing}
          data-testid="pms-connection-test"
          className="px-4 py-2 rounded-md text-[13px] font-medium bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-60"
        >
          {testing ? "Test en cours…" : "Tester la connexion"}
        </button>
        <Button
          onClick={() => { setConnected(true); toast.success("Configuration PMS enregistrée (sandbox)."); }}
          data-testid="pms-connection-save"
          className="text-white"
          style={{ backgroundColor: "var(--bp)" }}
        >
          Enregistrer
        </Button>
      </div>

      <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-[11.5px] text-blue-900 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-blue-600" />
        <div>
          Une fois connecté, chaque consultation dispose d&apos;un bouton <strong>&quot;Envoyer au PMS&quot;</strong> (icône <ArrowUpRight className="w-3 h-3 inline" style={{ color: "#4FC3F7" }} />).
          Les codes NGAP/CCAM proposés par défaut sont configurables dans <em>Administration → Paramétrage → Codes NGAP/CCAM par défaut</em>.
        </div>
      </div>
    </Card>
  );
}
